/**
 * Floating AI Assistant Button — glossy mark + ramp label matching WEB-CRM.
 */

import { openAIAssistant } from './ai-assistant-drawer.js';
import { createAssistantMark } from './utils/assistant-mark.js';
import { t } from '../../i18n.js';
import { isAiCreditLimitReached, openUsageLimitModal } from '../../services/domain/ai-credits.js';

/**
 * Create and initialize the floating AI button
 * @returns {HTMLElement} The floating button element
 */
export function createFloatingAIButton() {
  const button = document.createElement('button');
  button.className = 'floating-ai-button expanded';
  button.type = 'button';
  button.setAttribute('aria-label', t('ai_button_aria'));

  const glow = document.createElement('span');
  glow.className = 'floating-ai-glow';
  glow.setAttribute('aria-hidden', 'true');

  const sheen = document.createElement('span');
  sheen.className = 'floating-ai-sheen';
  sheen.setAttribute('aria-hidden', 'true');

  const mark = createAssistantMark({
    size: 40,
    className: 'floating-ai-mark floating-ai-mark--reveal',
  });

  const labelWrap = document.createElement('span');
  labelWrap.className = 'floating-ai-labelwrap';

  const textLabel = document.createElement('span');
  textLabel.className = 'floating-ai-text';
  textLabel.textContent = t('ai_button_label');
  labelWrap.appendChild(textLabel);

  button.appendChild(glow);
  button.appendChild(sheen);
  button.appendChild(mark);
  button.appendChild(labelWrap);

  let introOpen = true;
  const collapse = () => {
    introOpen = false;
    button.classList.remove('expanded');
    button.classList.add('collapsed');
  };

  const expand = () => {
    button.classList.add('expanded');
    button.classList.remove('collapsed');
  };

  button.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAiCreditLimitReached()) {
      openUsageLimitModal();
      return;
    }
    openAIAssistant();
  });

  const introTimer = setTimeout(collapse, 2500);

  button.addEventListener('mouseenter', () => {
    clearTimeout(introTimer);
    introOpen = false;
    expand();
  });
  button.addEventListener('mouseleave', () => {
    if (!introOpen) collapse();
  });
  button.addEventListener('focus', expand);
  button.addEventListener('blur', () => {
    if (!introOpen) collapse();
  });

  return button;
}

/**
 * Initialize the floating AI button and append to body
 */
export function initFloatingAIButton() {
  if (document.querySelector('.floating-ai-button')) {
    return;
  }

  const button = createFloatingAIButton();
  document.body.appendChild(button);

  return button;
}

export function removeFloatingAIButton() {
  document.querySelector('.floating-ai-button')?.remove();
}
